import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { superAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import {
  PageHeader, Badge, Btn, EmptyState,
  LoadingSpinner, ConfirmModal, FormField,
  inputStyle, selectStyle, TempPasswordBox,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function UsersPage() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [impersonatingId, setImpersonatingId] = useState(null);

  // Modals
  const [resetModal,      setResetModal]      = useState(null); // user
  const [confirmModal,    setConfirmModal]     = useState(null); // { type, user }
  const [actionLoading,   setActionLoading]    = useState(false);
  const [resetResult,     setResetResult]      = useState(null); // { password, email }

  const load = useCallback(() => {
    setLoading(true);
    superAdmin.listUsers()
      .then(r => setUsers(r.data?.results || r.data))
      .catch(() => toast.error("Failed to load users."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.facility_name || "").toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  // ── Reset password ───────────────────────────────────────────────────────
  const [newPw, setNewPw]   = useState("");
  const [pwMode, setPwMode] = useState("auto");

  const handleResetPassword = async () => {
    setActionLoading(true);
    try {
      const password = pwMode === "auto"
        ? generateTempPw()
        : newPw;

      await superAdmin.resetPassword(resetModal.id, { password });
      setResetResult({ password, email: resetModal.email });
      setResetModal(null);
      toast.success(`Password reset for ${resetModal.name}.`);
    } catch {
      toast.error("Failed to reset password.");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Impersonate ───────────────────────────────────────────────────────
  const ROLE_LANDING = {
    org_admin:      "/org",
    facility_admin: "/admin",
    staff:          "/staff",
  };

  const handleImpersonate = async (targetUser) => {
    setImpersonatingId(targetUser.id);
    try {
      const impersonated = await impersonate(targetUser.id);
      toast.success(`Viewing as ${targetUser.name}.`);
      navigate(ROLE_LANDING[impersonated.role] || "/");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to impersonate user.");
    } finally {
      setImpersonatingId(null);
    }
  };

  // ── Deactivate / Reactivate / Unlock ────────────────────────────────────
  const handleConfirmAction = async () => {
    if (!confirmModal) return;
    setActionLoading(true);
    try {
      const { type, user } = confirmModal;
      if (type === "deactivate") {
        await superAdmin.deactivateUser(user.id);
        toast.success(`${user.name} deactivated.`);
      } else if (type === "reactivate") {
        await superAdmin.reactivateUser(user.id);
        toast.success(`${user.name} reactivated.`);
      } else if (type === "unlock") {
        await superAdmin.unlockUser(user.id);
        toast.success(`${user.name} unlocked.`);
      }
      load();
    } catch {
      toast.error("Action failed.");
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
    }
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Users"
        subtitle={`${users.length} user${users.length !== 1 ? "s" : ""} across all facilities`}
      />

      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "16px" }}>
        Facility admin impersonation is now controlled per facility — see the "🎭 Impersonation"
        toggle on each facility in <strong>Facilities</strong>, not here.
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="Search by name, email, facility…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={{ ...styles.search, width: "160px" }}
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
        >
          <option value="all">All Roles</option>
          <option value="facility_admin">Facility Admin</option>
          <option value="staff">Staff</option>
        </select>
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <EmptyState icon="👥" title="No users found" subtitle="Try adjusting your search or filters." />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["User", "Role", "Facility", "Status", "Actions"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={styles.avatar}>
                        {u.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div style={styles.userName}>
                          {u.name}
                          {u.must_change_password && (
                            <span style={styles.pwChip} title="Must change password">🔑</span>
                          )}
                        </div>
                        <div style={styles.userEmail}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}><Badge value={u.role.replace("_", " ")} /></td>
                  <td style={styles.td}>
                    <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                      {u.facility_name || <span style={{ color: "var(--text-faint)" }}>—</span>}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <Badge value={u.is_active ? "active" : "inactive"} />
                    {u.locked_until && new Date(u.locked_until) > new Date() && (
                      <span style={{ marginLeft: "6px" }}>
                        <Badge value="locked" />
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {u.is_active && (
                        <Btn size="sm" variant="ghost"
                          onClick={() => handleImpersonate(u)}
                          disabled={impersonatingId === u.id}>
                          {impersonatingId === u.id ? "Loading…" : "Impersonate"}
                        </Btn>
                      )}
                      <Btn size="sm" variant="ghost"
                        onClick={() => { setResetModal(u); setPwMode("auto"); setNewPw(""); }}>
                        Reset PW
                      </Btn>
                      {u.is_active ? (
                        <Btn size="sm" variant="danger"
                          onClick={() => setConfirmModal({ type: "deactivate", user: u })}>
                          Deactivate
                        </Btn>
                      ) : (
                        <Btn size="sm" variant="success"
                          onClick={() => setConfirmModal({ type: "reactivate", user: u })}>
                          Reactivate
                        </Btn>
                      )}
                      {u.locked_until && new Date(u.locked_until) > new Date() && (
                        <Btn size="sm" variant="warning"
                          onClick={() => setConfirmModal({ type: "unlock", user: u })}>
                          Unlock
                        </Btn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      <Modal
        open={!!resetModal}
        onClose={() => setResetModal(null)}
        title="Reset Password"
        width="440px"
      >
        {resetModal && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={styles.userPreview}>
              <div style={styles.avatar}>{resetModal.name?.[0]?.toUpperCase()}</div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{resetModal.name}</div>
                <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{resetModal.email}</div>
              </div>
            </div>

            <FormField label="Password Method">
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ display: "flex", gap: "8px", cursor: "pointer", color: "var(--text-primary)", fontSize: "13px" }}>
                  <input type="radio" checked={pwMode === "auto"} onChange={() => setPwMode("auto")} />
                  Auto-generate secure temp password
                </label>
                <label style={{ display: "flex", gap: "8px", cursor: "pointer", color: "var(--text-primary)", fontSize: "13px" }}>
                  <input type="radio" checked={pwMode === "manual"} onChange={() => setPwMode("manual")} />
                  Set manually
                </label>
              </div>
            </FormField>

            {pwMode === "manual" && (
              <FormField label="New Password">
                <input style={inputStyle} type="password" value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  placeholder="Min. 8 characters" />
              </FormField>
            )}

            <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: 0 }}>
              User will be forced to change this password on next login.
            </p>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setResetModal(null)} disabled={actionLoading}>Cancel</Btn>
              <Btn onClick={handleResetPassword} disabled={actionLoading || (pwMode === "manual" && newPw.length < 8)}>
                {actionLoading ? "Resetting…" : "Reset Password"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Temp Password Result Modal ── */}
      <Modal
        open={!!resetResult}
        onClose={() => setResetResult(null)}
        title="Password Reset"
        width="440px"
      >
        {resetResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <TempPasswordBox password={resetResult.password} email={resetResult.email} />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={() => setResetResult(null)}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Confirm Action Modal ── */}
      <ConfirmModal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirmAction}
        loading={actionLoading}
        title={
          confirmModal?.type === "deactivate" ? "Deactivate User?" :
          confirmModal?.type === "reactivate" ? "Reactivate User?" :
          "Unlock Account?"
        }
        message={
          confirmModal?.type === "deactivate"
            ? `${confirmModal?.user?.name} will lose access to the platform immediately.`
            : confirmModal?.type === "reactivate"
            ? `${confirmModal?.user?.name} will regain access.`
            : `${confirmModal?.user?.name}'s account will be unlocked.`
        }
        confirmLabel={
          confirmModal?.type === "deactivate" ? "Deactivate" :
          confirmModal?.type === "reactivate" ? "Reactivate" : "Unlock"
        }
        variant={confirmModal?.type === "deactivate" ? "danger" : "success"}
      />
    </div>
  );
}

function generateTempPw() {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1200px" },
  toolbar: { display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" },
  search: {
    background: "var(--input-bg)",
    border: "1px solid var(--input-border)",
    borderRadius: "8px",
    padding: "9px 14px",
    color: "var(--text-primary)",
    fontSize: "14px",
    outline: "none",
    width: "280px",
    fontFamily: "inherit",
  },
  tableWrap: {
    background: "var(--surface-bg)",
    border: "1px solid var(--surface-border)",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "var(--card-shadow)",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "12px 16px",
    fontSize: "11px", fontWeight: "700", color: "var(--text-muted)",
    letterSpacing: "0.07em", textTransform: "uppercase",
    textAlign: "left", borderBottom: "1px solid var(--surface-border)",
    background: "var(--table-header-bg)",
  },
  tr: { borderBottom: "1px solid var(--surface-border)" },
  td: { padding: "14px 16px", verticalAlign: "middle" },
  avatar: {
    width: "32px", height: "32px", borderRadius: "8px",
    background: "linear-gradient(135deg, #408DC9, #7C3AED)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "13px", fontWeight: "700", color: "#fff", flexShrink: 0,
  },
  userName: { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "6px" },
  userEmail: { fontSize: "11px", color: "var(--text-muted)" },
  pwChip: { fontSize: "12px" },
  actions: { display: "flex", gap: "6px", flexWrap: "wrap" },
  userPreview: {
    display: "flex", alignItems: "center", gap: "12px",
    background: "var(--input-bg)", borderRadius: "8px", padding: "10px 12px",
  },
};
